// ============================================================================
// Forme normale EXACTE (lot 2 de la refonte, docs/moteur/REFONTE_MOTEUR.md).
//
// Un terme devient une FRACTION RATIONNELLE n ÷ d de polynômes à coefficients
// rationnels exacts sur des « atomes » :
//   - une inconnue (x, a, P(A)…) ;
//   - la racine d'un nombre premier (√2, √3) : √8 = 2√2, √2 × √2 = 2, √6 = √2 × √3 ;
//     les racines de nombres premiers distincts sont linéairement indépendantes
//     sur ℚ, donc deux écritures égales ont la même forme ;
//   - un sous-terme OPAQUE (cos(x), ln(2), 2^n, √(x + 1), π…), lui-même normalisé
//     à l'intérieur : ln(2x + 2x) et ln(4x) sont le même atome.
// Deux termes de même forme sont ÉGAUX (preuve exacte). Deux formes différentes ne
// prouvent RIEN quand des atomes opaques interviennent (cos² + sin² = 1) : c'est
// alors au contre-exemple de trancher.
// ============================================================================

import * as Q from './exact';
import type { Rat } from './exact';
import {
  canon,
  CONSTANTS,
  fn,
  involvesMatrix,
  num,
  pow as powT,
  substitute,
  sym,
  termKey,
  type FnName,
  type Sorts,
  type Term,
} from './term';

export type NormalFailCode = 'division_by_zero' | 'undefined_value' | 'too_big' | 'noncommutative';
export class NormalFail extends Error {
  constructor(public readonly code: NormalFailCode) {
    super(code);
  }
}

/** Taille maximale d'un polynôme (nombre de monômes) et d'un exposant développé. */
export const MAX_POLY_TERMS = 4000;
export const MAX_EXPANDED_POW = 24;
const MAX_ROOT_INPUT = 1e12;

/* ------------------------------ atomes ------------------------------ */

export interface Atom {
  /** clé stable : « $x », « √2 », « cos($x) » */
  key: string;
  kind: 'sym' | 'root' | 'opaque';
  /** écriture de l'atome (affichage, conditions) */
  term: Term;
  /** sans inconnue */
  constant: boolean;
  /** valeur approchée d'un atome constant */
  value?: number;
  /** nombre premier sous la racine (kind 'root') */
  prime?: number;
  /** nom de l'inconnue (kind 'sym') */
  symbol?: string;
}
interface Factor {
  a: Atom;
  e: number;
}
type Mono = Factor[];
interface PTerm {
  m: Mono;
  c: Rat;
}
/** Polynôme : monômes indexés par leur clé (« » pour la constante). */
export interface Poly {
  t: Map<string, PTerm>;
}
/** Fraction rationnelle n ÷ d. */
export interface RatFn {
  n: Poly;
  d: Poly;
}

function symAtom(name: string): Atom {
  if (CONSTANTS[name])
    return { key: `$${name}`, kind: 'opaque', term: sym(name), constant: true, value: Math.PI };
  return { key: `$${name}`, kind: 'sym', term: sym(name), constant: false, symbol: name };
}
function rootAtom(p: number): Atom {
  return {
    key: `√${p}`,
    kind: 'root',
    term: fn('sqrt', num(p)),
    constant: true,
    value: Math.sqrt(p),
    prime: p,
  };
}
function opaqueAtom(t: Term): Atom {
  const free = hasSymbol(t);
  const a: Atom = { key: termKey(t), kind: 'opaque', term: t, constant: !free };
  if (!free) a.value = evalFloat(t, {});
  return a;
}
function hasSymbol(t: Term): boolean {
  switch (t.k) {
    case 'num':
      return false;
    case 'sym':
      return !CONSTANTS[t.n];
    case 'add':
    case 'mul':
      return t.c.some(hasSymbol);
    case 'div':
    case 'pow':
    case 'binom':
      return hasSymbol(t.a) || hasSymbol(t.b);
    case 'fn':
      return hasSymbol(t.a);
  }
}

/* ------------------------------ monômes ------------------------------ */

function monoKey(m: Mono): string {
  return m.map((f) => (f.e === 1 ? f.a.key : `${f.a.key}^${f.e}`)).join('·');
}
/** Produit de deux monômes : √p × √p sort en coefficient p. */
function monoMul(x: Mono, y: Mono): { c: Rat; m: Mono } {
  const out: Factor[] = [];
  let c = Q.ONE;
  let i = 0;
  let j = 0;
  const push = (a: Atom, e: number): void => {
    if (a.kind === 'root') {
      const half = Math.floor(e / 2);
      if (half > 0) c = Q.mul(c, Q.pow(Q.rat(a.prime!), half)!);
      if (e % 2 === 1) out.push({ a, e: 1 });
    } else out.push({ a, e });
  };
  while (i < x.length || j < y.length) {
    const fx = x[i];
    const fy = y[j];
    if (fx && (!fy || fx.a.key < fy.a.key)) {
      push(fx.a, fx.e);
      i++;
    } else if (fy && (!fx || fy.a.key < fx.a.key)) {
      push(fy.a, fy.e);
      j++;
    } else {
      push(fx!.a, fx!.e + fy!.e);
      i++;
      j++;
    }
  }
  return { c, m: out };
}

/** Ordre lexicographique (atome de plus petite clé = variable principale). */
function monoCmp(x: Mono, y: Mono): number {
  let i = 0;
  let j = 0;
  while (i < x.length || j < y.length) {
    const fx = x[i];
    const fy = y[j];
    if (fx && (!fy || fx.a.key < fy.a.key)) return 1;
    if (fy && (!fx || fy.a.key < fx.a.key)) return -1;
    if (fx!.e !== fy!.e) return fx!.e > fy!.e ? 1 : -1;
    i++;
    j++;
  }
  return 0;
}
function monoDiv(x: Mono, y: Mono): Mono | null {
  const out: Factor[] = [];
  let j = 0;
  for (const f of x) {
    const g = y[j];
    if (g && g.a.key < f.a.key) return null;
    if (g && g.a.key === f.a.key) {
      if (g.e > f.e) return null;
      if (f.e > g.e) out.push({ a: f.a, e: f.e - g.e });
      j++;
    } else out.push(f);
  }
  return j === y.length ? out : null;
}
function monoDegree(m: Mono): number {
  return m.reduce((s, f) => s + f.e, 0);
}

/* ------------------------------ polynômes ------------------------------ */

export const ZERO_POLY: Poly = { t: new Map() };
export function constPoly(c: Rat): Poly {
  return Q.isZero(c) ? { t: new Map() } : { t: new Map([['', { m: [], c }]]) };
}
function atomPoly(a: Atom): Poly {
  const m = [{ a, e: 1 }];
  return { t: new Map([[monoKey(m), { m, c: Q.ONE }]]) };
}
function addTerm(map: Map<string, PTerm>, m: Mono, c: Rat): void {
  if (Q.isZero(c)) return;
  const k = monoKey(m);
  const old = map.get(k);
  if (!old) map.set(k, { m, c });
  else {
    const s = Q.add(old.c, c);
    if (Q.isZero(s)) map.delete(k);
    else map.set(k, { m, c: s });
  }
}
export function polyAdd(p: Poly, q: Poly): Poly {
  const t = new Map(p.t);
  for (const { m, c } of q.t.values()) addTerm(t, m, c);
  if (t.size > MAX_POLY_TERMS) throw new NormalFail('too_big');
  return { t };
}
export function polyScale(p: Poly, k: Rat): Poly {
  if (Q.isZero(k)) return ZERO_POLY;
  const t = new Map<string, PTerm>();
  for (const [key, { m, c }] of p.t) t.set(key, { m, c: Q.mul(c, k) });
  return { t };
}
export function polyNeg(p: Poly): Poly {
  return polyScale(p, Q.rat(-1));
}
export function polySub(p: Poly, q: Poly): Poly {
  return polyAdd(p, polyNeg(q));
}
export function polyMul(p: Poly, q: Poly): Poly {
  if (p.t.size * q.t.size > MAX_POLY_TERMS * 8) throw new NormalFail('too_big');
  const t = new Map<string, PTerm>();
  for (const x of p.t.values())
    for (const y of q.t.values()) {
      const r = monoMul(x.m, y.m);
      addTerm(t, r.m, Q.mul(Q.mul(x.c, y.c), r.c));
    }
  if (t.size > MAX_POLY_TERMS) throw new NormalFail('too_big');
  return { t };
}
export function polyPow(p: Poly, k: number): Poly {
  let out = constPoly(Q.ONE);
  for (let i = 0; i < k; i++) out = polyMul(out, p);
  return out;
}
export function polyIsZero(p: Poly): boolean {
  return p.t.size === 0;
}
export function polyEqual(p: Poly, q: Poly): boolean {
  return polyIsZero(polySub(p, q));
}
/** Valeur rationnelle d'un polynôme sans atome, sinon null. */
export function polyRat(p: Poly): Rat | null {
  if (p.t.size === 0) return Q.ZERO;
  if (p.t.size === 1 && p.t.has('')) return p.t.get('')!.c;
  return null;
}
/** Sans inconnue (atomes constants seulement : √2, π, ln(2)…). */
export function polyIsConstant(p: Poly): boolean {
  for (const { m } of p.t.values()) if (m.some((f) => !f.a.constant)) return false;
  return true;
}
/** Valeur approchée d'un polynôme constant. */
export function polyFloat(p: Poly): number {
  let s = 0;
  for (const { m, c } of p.t.values())
    s += Q.ratToNumber(c) * m.reduce((v, f) => v * f.a.value! ** f.e, 1);
  return s;
}
/** Inconnues présentes. */
export function polySymbols(p: Poly): Set<string> {
  const out = new Set<string>();
  for (const { m } of p.t.values())
    for (const f of m) {
      if (f.a.kind === 'sym') out.add(f.a.symbol!);
      else if (!f.a.constant) for (const s of termSymbols(f.a.term)) out.add(s);
    }
  return out;
}
function termSymbols(t: Term, acc = new Set<string>()): Set<string> {
  if (t.k === 'sym') {
    if (!CONSTANTS[t.n]) acc.add(t.n);
  } else if (t.k === 'add' || t.k === 'mul') t.c.forEach((c) => termSymbols(c, acc));
  else if (t.k === 'div' || t.k === 'pow') {
    termSymbols(t.a, acc);
    termSymbols(t.b, acc);
  } else if (t.k === 'fn') termSymbols(t.a, acc);
  return acc;
}
/** Le polynôme contient-il un atome opaque non constant (cos(x), √(x + 1)…) ? */
export function polyHasOpaque(p: Poly): boolean {
  for (const { m } of p.t.values())
    if (m.some((f) => f.a.kind === 'opaque' && !f.a.constant)) return true;
  return false;
}
function leading(p: Poly): PTerm | null {
  let best: PTerm | null = null;
  for (const x of p.t.values()) if (!best || monoCmp(x.m, best.m) > 0) best = x;
  return best;
}
/** Monômes triés (degré décroissant, puis ordre lexicographique). */
export function polyTerms(p: Poly): { m: Mono; c: Rat }[] {
  return [...p.t.values()].sort((x, y) => monoDegree(y.m) - monoDegree(x.m) || monoCmp(y.m, x.m));
}

/** Division EXACTE p ÷ q (null si q ne divise pas p). Le quotient est revérifié. */
export function polyDivExact(p: Poly, q: Poly): Poly | null {
  if (polyIsZero(q)) return null;
  const lq = leading(q)!;
  let r = p;
  let quo: Poly = ZERO_POLY;
  for (let guard = 0; guard < 2000; guard++) {
    if (polyIsZero(r)) return polyEqual(polyMul(quo, q), p) ? quo : null;
    const lr = leading(r)!;
    const m = monoDiv(lr.m, lq.m);
    if (!m) return null;
    const step: Poly = { t: new Map([[monoKey(m), { m, c: Q.div(lr.c, lq.c)! }]]) };
    quo = polyAdd(quo, step);
    const next = polySub(r, polyMul(step, q));
    if (next.t.has(monoKey(lr.m)) && Q.eq(next.t.get(monoKey(lr.m))!.c, lr.c)) return null;
    r = next;
  }
  return null;
}

/** pgcd des coefficients (rationnel positif) : 6x + 4 → 2 ; x ÷ 2 + 1 ÷ 3 → 1/6. */
export function polyContent(p: Poly): Rat {
  let n = 0n;
  let d = 1n;
  const gcd = (a: bigint, b: bigint): bigint => (b === 0n ? (a < 0n ? -a : a) : gcd(b, a % b));
  for (const { c } of p.t.values()) {
    n = gcd(n, c.n);
    d = (d / gcd(d, c.d)) * c.d;
  }
  return n === 0n ? Q.ONE : Q.rat(n, d);
}

/* --------------------------- fractions rationnelles --------------------------- */

const ONE_POLY = (): Poly => constPoly(Q.ONE);
function rf(n: Poly, d: Poly = ONE_POLY()): RatFn {
  const k = polyRat(d);
  if (k !== null) {
    if (Q.isZero(k)) throw new NormalFail('division_by_zero');
    return { n: polyScale(n, Q.div(Q.ONE, k)!), d: ONE_POLY() };
  }
  return { n, d };
}
function sameDen(a: Poly, b: Poly): boolean {
  return polyEqual(a, b);
}
export function rfAdd(a: RatFn, b: RatFn): RatFn {
  if (sameDen(a.d, b.d)) return rf(polyAdd(a.n, b.n), a.d);
  return rf(polyAdd(polyMul(a.n, b.d), polyMul(b.n, a.d)), polyMul(a.d, b.d));
}
export function rfSub(a: RatFn, b: RatFn): RatFn {
  return rfAdd(a, { n: polyNeg(b.n), d: b.d });
}
export function rfMul(a: RatFn, b: RatFn): RatFn {
  return rf(polyMul(a.n, b.n), polyMul(a.d, b.d));
}
export function rfDiv(a: RatFn, b: RatFn): RatFn {
  if (polyIsZero(b.n)) throw new NormalFail('division_by_zero');
  return rf(polyMul(a.n, b.d), polyMul(a.d, b.n));
}
export function rfIsZero(a: RatFn): boolean {
  return polyIsZero(a.n);
}
export function rfEqual(a: RatFn, b: RatFn): boolean {
  return polyEqual(polyMul(a.n, b.d), polyMul(b.n, a.d));
}
/** Valeur rationnelle si la fraction est un nombre, sinon null. */
export function rfRat(a: RatFn): Rat | null {
  const n = polyRat(a.n);
  const d = polyRat(a.d);
  return n !== null && d !== null ? Q.div(n, d) : null;
}

/** Simplification : contenu, monôme commun, division exacte. */
export function rfSimplify(a: RatFn): RatFn {
  let { n, d } = a;
  if (polyRat(d) !== null) return rf(n, d);
  // monôme commun
  const common = commonMono([...n.t.values(), ...d.t.values()]);
  if (common.length) {
    n = divideMono(n, common);
    d = divideMono(d, common);
  }
  const q = polyDivExact(n, d);
  if (q) return rf(q);
  // dénominateur unitaire : coefficient dominant 1
  const ld = leading(d)!;
  return { n: polyScale(n, Q.div(Q.ONE, ld.c)!), d: polyScale(d, Q.div(Q.ONE, ld.c)!) };
}
function commonMono(terms: PTerm[]): Mono {
  if (!terms.length) return [];
  let acc: Mono = terms[0]!.m.filter((f) => f.a.kind !== 'root');
  for (const x of terms.slice(1)) {
    acc = acc
      .map((f) => {
        const g = x.m.find((h) => h.a.key === f.a.key);
        return g ? { a: f.a, e: Math.min(f.e, g.e) } : null;
      })
      .filter((f): f is Factor => f !== null);
  }
  return acc;
}
function divideMono(p: Poly, m: Mono): Poly {
  const t = new Map<string, PTerm>();
  for (const x of p.t.values()) {
    const q = monoDiv(x.m, m)!;
    t.set(monoKey(q), { m: q, c: x.c });
  }
  return { t };
}

/* ------------------------------ terme → forme ------------------------------ */

export interface NormalOptions {
  sorts?: Sorts;
}

/** Forme normale d'un terme. Lève `NormalFail` (division par zéro, trop gros, matrices). */
export function toRatFn(t: Term, opts: NormalOptions = {}): RatFn {
  const r = conv(canon(t, opts.sorts ?? {}), opts.sorts ?? {});
  // une factorielle symbolique (n!) est un atome : 2 parmi n = n! ÷ (2 × (n − 2)!) laisse
  // n! en haut ET en bas. On le simplifie, sinon l'étude en une inconnue ne voit pas le
  // polynôme n(n − 1) ÷ 2 (lot 5.6).
  return hasFactAtom(r) ? rfSimplify(r) : r;
}

function hasFactAtom(r: RatFn): boolean {
  for (const p of [r.n, r.d])
    for (const x of p.t.values()) if (x.m.some((f) => f.a.key.startsWith('fact('))) return true;
  return false;
}

/** Variante sans exception. */
export function normalize(
  t: Term,
  opts: NormalOptions = {},
): { ok: true; rf: RatFn } | { ok: false; code: NormalFailCode } {
  try {
    return { ok: true, rf: toRatFn(t, opts) };
  } catch (e) {
    if (e instanceof NormalFail) return { ok: false, code: e.code };
    throw e;
  }
}

function conv(t: Term, sorts: Sorts): RatFn {
  switch (t.k) {
    case 'num':
      return rf(constPoly(t.v));
    case 'sym':
      return rf(atomPoly(symAtom(t.n)));
    case 'add':
      return t.c.map((c) => conv(c, sorts)).reduce(rfAdd);
    case 'mul': {
      if (t.c.filter((c) => involvesMatrix(c, sorts)).length > 1)
        throw new NormalFail('noncommutative');
      return t.c.map((c) => conv(c, sorts)).reduce(rfMul);
    }
    case 'div':
      return rfDiv(conv(t.a, sorts), conv(t.b, sorts));
    case 'pow':
      return convPow(t.a, t.b, sorts);
    case 'fn':
      return convFn(t.f, t.a, sorts);
    case 'binom':
      return convBinom(t.a, t.b, sorts);
  }
}

/* ---------------------- factorielles (moteur v27) ---------------------- */

/** Décalage maximal d'une factorielle à sa base : (n + 64)! ou (n − 64)! au plus. */
const MAX_FACT_SHIFT = 64;
/** Plus grand entier dont la factorielle se calcule (au-delà : trop grand). */
const MAX_FACT_VALUE = 1000n;

function factBig(n: bigint): bigint {
  let f = 1n;
  for (let i = 2n; i <= n; i++) f *= i;
  return f;
}

/**
 * Factorielle EXACTE. Un entier naturel se calcule (100! compris) ; un nombre négatif
 * ou non entier n'a pas de factorielle. Avec une inconnue, chaque factorielle s'écrit
 * à partir de celle de sa BASE (l'argument sans sa constante entière) :
 * (n − 2)! = n! ÷ (n(n − 1)), (n + 1)! = (n + 1) × n!. Deux écritures égales ont donc
 * la même forme : n! ÷ (n − 1)! = n, (k + 1)! = (k + 1) × k!. Les identités ne valent
 * que là où les factorielles existent (n ≥ 2 pour (n − 2)!) : c'est aux hypothèses
 * de la feuille (n entier naturel) d'en garder le domaine.
 */
function factOf(inner: RatFn): RatFn {
  const v = rfRat(inner);
  if (v !== null) {
    if (!Q.isInteger(v) || v.n < 0n) throw new NormalFail('undefined_value');
    if (v.n > MAX_FACT_VALUE) throw new NormalFail('too_big');
    return rf(constPoly(Q.rat(factBig(v.n))));
  }
  const d = polyRat(inner.d);
  const opaque = (): RatFn => rf(atomPoly(opaqueAtom(fn('fact', rfToTerm(rfSimplify(inner))))));
  if (d === null) return opaque();
  const p = polyScale(inner.n, Q.div(Q.ONE, d)!);
  const c = polyRat({ t: new Map([...p.t].filter(([k]) => k === '')) }) ?? Q.ZERO;
  if (!Q.isInteger(c) || Q.abs(c).n > BigInt(MAX_FACT_SHIFT)) return opaque();
  const base = polySub(p, constPoly(c));
  const F = rf(atomPoly(opaqueAtom(fn('fact', polyToTerm(base)))));
  const shift = Number(c.n);
  let out = F;
  // (base + c)! = base! × (base + 1) … (base + c) ; (base − c)! = base! ÷ (base − c + 1) … base
  for (let j = 1; j <= Math.abs(shift); j++) {
    const k = shift > 0 ? j : 1 - j;
    const f = rf(polyAdd(base, constPoly(Q.rat(k))));
    out = shift > 0 ? rfMul(out, f) : rfDiv(out, f);
  }
  return out;
}

/** « k parmi n » = n! ÷ (k! × (n − k)!) (moteur v27) ; 0 si k > n (entiers). */
function convBinom(k: Term, n: Term, sorts: Sorts): RatFn {
  const kr = rfSimplify(conv(k, sorts));
  const nr = rfSimplify(conv(n, sorts));
  const kv = rfRat(kr);
  const nv = rfRat(nr);
  if (kv !== null && nv !== null) {
    if (!Q.isInteger(kv) || !Q.isInteger(nv) || kv.n < 0n || nv.n < 0n)
      throw new NormalFail('undefined_value');
    if (kv.n > nv.n) return rf(ZERO_POLY);
  }
  if (kv !== null && (!Q.isInteger(kv) || kv.n < 0n)) throw new NormalFail('undefined_value');
  return rfDiv(factOf(nr), rfMul(factOf(kr), factOf(rfSub(nr, kr))));
}

/** Écriture normalisée d'un sous-terme (clé des atomes opaques). */
function innerTerm(t: Term, sorts: Sorts): Term {
  return rfToTerm(rfSimplify(conv(t, sorts)));
}

function convPow(a: Term, b: Term, sorts: Sorts): RatFn {
  const base = conv(a, sorts);
  const ex = conv(b, sorts);
  const k = rfRat(ex);
  if (k !== null && Q.isInteger(k)) {
    const e = Number(k.n);
    if (Math.abs(e) > MAX_EXPANDED_POW) {
      const br = rfRat(base);
      if (br !== null) {
        const p = Q.pow(br, e);
        if (p === null) throw new NormalFail(Q.isZero(br) ? 'division_by_zero' : 'too_big');
        return rf(constPoly(p));
      }
      throw new NormalFail('too_big');
    }
    const pn = polyPow(base.n, Math.abs(e));
    const pd = polyPow(base.d, Math.abs(e));
    return e >= 0 ? rf(pn, pd) : rfDiv(rf(ONE_POLY()), rf(pn, pd));
  }
  const br = rfRat(base);
  if (k !== null && br !== null) {
    const root = exactRationalPower(br, k);
    if (root) return root;
  }
  // exposant non rationnel, base rationnelle > 0 : bᵘ = exp(u ln b) (lot 3 bis :
  // 3^(ln 5 ÷ ln 3) = 5 se vérifie exactement, et 2^(n − 1) = 2ⁿ ÷ 2)
  if (k === null && br !== null && Q.sign(br) > 0)
    return convFn('exp', { k: 'mul', c: [b, { k: 'fn', f: 'ln', a }] }, sorts);
  return rf(atomPoly(opaqueAtom(powT(innerTerm(a, sorts), innerTerm(b, sorts)))));
}

/** q^(p/r) exact : 125^(1/3) = 5, 8^(2/3) = 4, 2^(1/2) = √2. */
function exactRationalPower(q: Rat, k: Rat): RatFn | null {
  if (q.n < 0n) return null;
  const r = Number(k.d);
  if (r === 2) {
    const rad = sqrtOfRat(Q.pow(q, Number(k.n))!);
    return rad;
  }
  const nr = intRoot(q.n, r);
  const dr = intRoot(q.d, r);
  if (nr === null || dr === null) return null;
  const p = Q.pow(Q.rat(nr, dr), Number(k.n));
  return p ? rf(constPoly(p)) : null;
}
function intRoot(n: bigint, r: number): bigint | null {
  if (n < 0n || r < 2 || r > 64) return null;
  if (n < 2n) return n;
  const x = BigInt(Math.round(Number(n) ** (1 / r)));
  for (const c of [x - 1n, x, x + 1n]) if (c >= 0n && c ** BigInt(r) === n) return c;
  return null;
}

/** √q pour q rationnel ≥ 0 : coefficient × produit de racines de nombres premiers. */
function sqrtOfRat(q: Rat): RatFn | null {
  if (q.n < 0n) throw new NormalFail('undefined_value');
  const rad = Q.sqrtRat(q);
  if (!rad) return null;
  if (rad.rad === 1n) return rf(constPoly(rad.coef));
  if (rad.rad > BigInt(MAX_ROOT_INPUT)) return null;
  const primes = primeFactors(Number(rad.rad));
  if (!primes) return null;
  let p = constPoly(rad.coef);
  for (const pr of primes) p = polyMul(p, atomPoly(rootAtom(pr)));
  return rf(p);
}
/** Facteurs premiers (sans carré) d'un entier ≤ 10¹² ; null au-delà. */
function primeFactors(n: number): number[] | null {
  if (n > MAX_ROOT_INPUT) return null;
  const out: number[] = [];
  let m = n;
  for (let p = 2; p * p <= m; p += p === 2 ? 1 : 2) {
    while (m % p === 0) {
      out.push(p);
      m /= p;
    }
  }
  if (m > 1) out.push(m);
  return out;
}

const EXACT_AT_ZERO: Partial<Record<FnName, Rat>> = {
  exp: Q.ONE,
  cos: Q.ONE,
  sin: Q.ZERO,
  tan: Q.ZERO,
  arcsin: Q.ZERO,
  arctan: Q.ZERO,
};

/* ---- valeurs exactes (lot 3 bis) ---- */

/** k tel que r = k·π exactement (k rationnel), sinon null. */
function piMultiple(r: RatFn): Rat | null {
  const d = polyRat(r.d);
  if (d === null) return null;
  const terms = [...r.n.t.values()];
  if (!terms.length) return Q.ZERO;
  if (terms.length !== 1) return null;
  const t = terms[0]!;
  if (t.m.length !== 1 || t.m[0]!.a.key !== '$π' || t.m[0]!.e !== 1) return null;
  return Q.div(t.c, d);
}
const rootRf = (p: number, k: Rat): RatFn => rf(polyScale(atomPoly(rootAtom(p)), k));
const ratRf = (q: Rat): RatFn => rf(constPoly(q));
/** cos d°, d entier multiple de 30 ou de 45, valeur exacte (racines de 2 et 3). */
function cosDeg(d0: number): RatFn {
  const d = ((d0 % 360) + 360) % 360;
  const [ref, sign] =
    d <= 90 ? [d, 1] : d <= 180 ? [180 - d, -1] : d <= 270 ? [d - 180, -1] : [360 - d, 1];
  const s = Q.rat(sign);
  switch (ref) {
    case 0:
      return ratRf(s);
    case 30:
      return rootRf(3, Q.mul(s, Q.rat(1, 2)));
    case 45:
      return rootRf(2, Q.mul(s, Q.rat(1, 2)));
    case 60:
      return ratRf(Q.mul(s, Q.rat(1, 2)));
    default:
      return ratRf(Q.ZERO);
  }
}
/** Angle remarquable en degrés (multiple de 30° ou de 45°), sinon null. */
function remarkableDegrees(angle: RatFn): number | null {
  const k = piMultiple(angle);
  if (k === null) return null;
  const twelfths = Q.mul(k, Q.rat(12));
  if (!Q.isInteger(twelfths)) return null;
  const n = Number(twelfths.n);
  if (!Number.isSafeInteger(n) || (n % 2 !== 0 && n % 3 !== 0)) return null;
  return n * 15;
}
function trigExact(f: 'cos' | 'sin' | 'tan', angle: RatFn): RatFn | null {
  const d = remarkableDegrees(angle);
  if (d === null) return null;
  if (f === 'cos') return cosDeg(d);
  if (f === 'sin') return cosDeg(90 - d);
  const c = cosDeg(d);
  if (polyIsZero(c.n)) throw new NormalFail('undefined_value');
  return rfSimplify(rfDiv(cosDeg(90 - d), c));
}
const sameRf = (a: RatFn, b: RatFn): boolean =>
  polyIsZero(polySub(polyMul(a.n, b.d), polyMul(b.n, a.d)));
/** arccos, arcsin, arctan d'une valeur remarquable, EN DEGRÉS (comme le moteur actuel). */
function arcExact(f: 'arccos' | 'arcsin' | 'arctan', v: RatFn): RatFn | null {
  const range =
    f === 'arccos'
      ? [0, 30, 45, 60, 90, 120, 135, 150, 180]
      : f === 'arcsin'
        ? [-90, -60, -45, -30, 0, 30, 45, 60, 90]
        : [-60, -45, -30, 0, 30, 45, 60];
  for (const d of range) {
    const val =
      f === 'arccos'
        ? cosDeg(d)
        : f === 'arcsin'
          ? cosDeg(90 - d)
          : rfSimplify(rfDiv(cosDeg(90 - d), cosDeg(d)));
    if (sameRf(v, val)) return ratRf(Q.rat(d));
  }
  return null;
}
/** Facteurs premiers avec multiplicité d'un entier ≤ 10¹² (null au-delà). */
function primePowers(n: bigint): Map<number, number> | null {
  if (n > BigInt(MAX_ROOT_INPUT)) return null;
  const out = new Map<number, number>();
  for (const p of primeFactors(Number(n)) ?? []) out.set(p, (out.get(p) ?? 0) + 1);
  return out;
}
const lnAtom = (p: number): Atom => opaqueAtom(fn('ln', num(p)));
/** ln q = Σ e·ln p sur les nombres premiers (q rationnel > 0). */
function lnOfRat(q: Rat): RatFn | null {
  const num_ = primePowers(q.n);
  const den = primePowers(q.d);
  if (!num_ || !den) return null;
  let p = ZERO_POLY;
  for (const [pr, e] of num_) p = polyAdd(p, polyScale(atomPoly(lnAtom(pr)), Q.rat(e)));
  for (const [pr, e] of den) p = polyAdd(p, polyScale(atomPoly(lnAtom(pr)), Q.rat(-e)));
  return rf(p);
}
/**
 * ln d'un quotient de deux monômes POSITIFS : c × √p × exp(u)ᵏ… (lot 3 bis :
 * ln(1 ÷ exp(1)) = −1, ln(2 exp(x)) = ln 2 + x). null sinon (une lettre au signe
 * inconnu, une autre fonction).
 */
function lnOfConstant(r: RatFn, sorts: Sorts): RatFn | null {
  const nt = [...r.n.t.values()];
  const dt = [...r.d.t.values()];
  if (nt.length !== 1 || dt.length !== 1) return null;
  const c = Q.div(nt[0]!.c, dt[0]!.c)!;
  if (Q.sign(c) <= 0) return null;
  let acc = lnOfRat(c);
  if (!acc) return null;
  for (const [mono, sign] of [
    [nt[0]!.m, 1],
    [dt[0]!.m, -1],
  ] as const)
    for (const f of mono) {
      const k = Q.rat(sign * f.e);
      if (f.a.kind === 'root')
        acc = rfAdd(acc, rf(polyScale(atomPoly(lnAtom(f.a.prime!)), Q.mul(k, Q.rat(1, 2)))));
      else if (f.a.kind === 'opaque' && f.a.term.k === 'fn' && f.a.term.f === 'exp')
        acc = rfAdd(acc, rfMul(rf(constPoly(k)), conv(f.a.term.a, sorts)));
      else return null;
    }
  return acc;
}
const expAtom = (t: Term): Atom => opaqueAtom(fn('exp', t));
function atomPower(a: Atom, k: number): RatFn {
  const p = polyPow(atomPoly(a), Math.abs(k));
  return k >= 0 ? rf(p) : rfDiv(rf(ONE_POLY()), rf(p));
}
/**
 * exp d'une somme = produit des exp : exp(2 ln 2) = 4, exp(−ln 2) = 1/2,
 * exp(x + 2) = exp(x) × e², exp(2x) = exp(x)². Le reste (exp(x/2)…) reste opaque.
 */
function expOf(inner: RatFn): RatFn | null {
  const d = polyRat(inner.d);
  if (d === null) return null;
  let acc = rf(ONE_POLY());
  const rest: PTerm[] = [];
  for (const t of inner.n.t.values()) {
    const r = Q.div(t.c, d)!;
    const small = Q.isInteger(r) && Math.abs(Number(r.n)) <= MAX_EXPANDED_POW;
    if (!t.m.length) {
      if (small) acc = rfMul(acc, atomPower(expAtom(num(1)), Number(r.n)));
      else rest.push({ m: [], c: r });
      continue;
    }
    const f = t.m[0]!;
    const lnPrime =
      t.m.length === 1 &&
      f.e === 1 &&
      f.a.term.k === 'fn' &&
      f.a.term.f === 'ln' &&
      f.a.term.a.k === 'num'
        ? f.a.term.a.v
        : null;
    if (lnPrime && small) {
      acc = rfMul(acc, ratRf(Q.pow(lnPrime, Number(r.n))!));
      continue;
    }
    if (lnPrime) {
      const p = exactRationalPower(lnPrime, r);
      if (p) {
        acc = rfMul(acc, p);
        continue;
      }
    }
    if (small) {
      const m = polyToTerm({ t: new Map([[monoKey(t.m), { m: t.m, c: Q.ONE }]]) });
      acc = rfMul(acc, atomPower(expAtom(m), Number(r.n)));
    } else rest.push({ m: t.m, c: r });
  }
  if (rest.length) {
    const p: Poly = { t: new Map(rest.map((x) => [monoKey(x.m), x])) };
    acc = rfMul(acc, rf(atomPoly(expAtom(polyToTerm(p)))));
  }
  return acc;
}

function convFn(f: FnName, a: Term, sorts: Sorts): RatFn {
  // moteur v45 : log(u) = ln(u) ÷ ln(10)
  if (f === 'log')
    return conv(
      { k: 'div', a: { k: 'fn', f: 'ln', a }, b: { k: 'fn', f: 'ln', a: num(10) } },
      sorts,
    );
  // ln(exp(t)) = t ; exp(ln(c)) = c pour une constante c > 0
  if (f === 'ln' && a.k === 'fn' && a.f === 'exp') return conv(a.a, sorts);
  if (f === 'exp' && a.k === 'fn' && a.f === 'ln') {
    const c = rfSimplify(conv(a.a, sorts));
    if (polyIsConstant(c.n) && polyIsConstant(c.d)) {
      const x = polyFloat(c.n) / polyFloat(c.d);
      if (Number.isFinite(x) && x > 1e-9) return c;
    }
  }
  const inner = rfSimplify(conv(a, sorts));
  const v = rfRat(inner);
  switch (f) {
    case 'cos':
    case 'sin':
    case 'tan': {
      const t = trigExact(f, inner);
      if (t) return t;
      break;
    }
    case 'arccos':
    case 'arcsin':
    case 'arctan': {
      if (v !== null && f !== 'arctan' && Q.ratToNumber(Q.abs(v)) > 1)
        throw new NormalFail('undefined_value');
      const t = arcExact(f, inner);
      if (t) return t;
      break;
    }
    case 'exp': {
      const e = expOf(inner);
      if (e) return e;
      break;
    }
  }
  switch (f) {
    case 'sqrt': {
      if (v !== null) {
        const r = sqrtOfRat(v);
        if (r) return r;
      }
      break;
    }
    case 'abs': {
      if (v !== null) return rf(constPoly(Q.abs(v)));
      if (polyIsConstant(inner.n) && polyIsConstant(inner.d)) {
        const x = polyFloat(inner.n) / polyFloat(inner.d);
        if (Math.abs(x) > 1e-9) return x > 0 ? inner : rfMul(rf(constPoly(Q.rat(-1))), inner);
      }
      break;
    }
    case 'deg':
      return rfMul(inner, rf(polyScale(atomPoly(symAtom('π')), Q.rat(1, 180))));
    case 'fact':
      return factOf(inner);
    case 'ln': {
      if (v !== null && Q.sign(v) <= 0) throw new NormalFail('undefined_value');
      if (v !== null && Q.eq(v, Q.ONE)) return rf(ZERO_POLY);
      const l = v !== null ? lnOfRat(v) : lnOfConstant(inner, sorts);
      if (l) return l;
      break;
    }
    default:
      if (v !== null && Q.isZero(v) && EXACT_AT_ZERO[f]) return rf(constPoly(EXACT_AT_ZERO[f]!));
  }
  return rf(atomPoly(opaqueAtom(fn(f, rfToTerm(rfSimplify(inner))))));
}

/* ------------------------------ forme → terme ------------------------------ */

function monoTerm(m: Mono): Term[] {
  const out: Term[] = [];
  let radicand = 1;
  for (const f of m) {
    if (f.a.kind === 'root') radicand *= f.a.prime!;
    else out.push(f.e === 1 ? f.a.term : powT(f.a.term, num(f.e)));
  }
  if (radicand > 1) out.unshift(fn('sqrt', num(radicand)));
  return out;
}
export function polyToTerm(p: Poly): Term {
  const terms = polyTerms(p).map(({ m, c }) => {
    const fs = monoTerm(m);
    if (!fs.length) return num(c);
    return Q.eq(c, Q.ONE)
      ? fs.length === 1
        ? fs[0]!
        : ({ k: 'mul', c: fs } as Term)
      : ({ k: 'mul', c: [num(c), ...fs] } as Term);
  });
  if (!terms.length) return num(0);
  return canon(terms.length === 1 ? terms[0]! : { k: 'add', c: terms });
}
export function rfToTerm(a: RatFn): Term {
  const d = polyRat(a.d);
  if (d !== null) return polyToTerm(polyScale(a.n, Q.div(Q.ONE, d)!));
  return canon({ k: 'div', a: polyToTerm(a.n), b: polyToTerm(a.d) });
}

/* ------------------------------ comparaisons ------------------------------ */

export type Equality = 'equal' | 'different' | 'undecided';

/**
 * Égalité de deux termes : 'equal' = prouvé exactement ; 'different' = formes
 * différentes SANS atome opaque non constant (donc fonctions différentes) ;
 * 'undecided' sinon (au contre-exemple de trancher).
 */
export function compareTerms(a: Term, b: Term, opts: NormalOptions = {}): Equality {
  const x = normalize(a, opts);
  const y = normalize(b, opts);
  if (!x.ok || !y.ok) return 'undecided';
  const diff = polySub(polyMul(x.rf.n, y.rf.d), polyMul(y.rf.n, x.rf.d));
  if (polyIsZero(diff)) return 'equal';
  return polyHasOpaque(diff) || hasNonRootConstant(diff) ? 'undecided' : 'different';
}
/** Constantes autres que racines et π (ln(4) et 2ln(2) ne sont pas distinguées par la forme). */
function hasNonRootConstant(p: Poly): boolean {
  for (const { m } of p.t.values())
    if (m.some((f) => f.a.kind === 'opaque' && f.a.constant && f.a.key !== '$π')) return true;
  return false;
}

/* ------------------------------ évaluations ------------------------------ */

/** Valeur exacte en des valeurs rationnelles des inconnues (null : irrationnelle ou opaque). */
export function evalExact(
  t: Term,
  env: Readonly<Record<string, Rat>>,
  opts: NormalOptions = {},
): Rat | null {
  const map: Record<string, Term> = {};
  for (const [k, v] of Object.entries(env)) map[k] = num(v);
  return rfRat(toRatFn(substitute(t, map), opts));
}

/*
 * Moteur v60 (lot 6.65) : arctan, arcsin, arccos s'évaluent EN RADIANS à l'intérieur de
 * `withRadianArcs` (intégrales, sommes de Riemann, dérivées qui les écrivent) ; ailleurs en
 * degrés, comme toujours (convention des triangles).
 */
let RADIAN_ARCS = false;
/** exécute `f` avec arctan, arcsin, arccos évalués en radians */
export function withRadianArcs<T>(f: () => T): T {
  const prev = RADIAN_ARCS;
  RADIAN_ARCS = true;
  try {
    return f();
  } finally {
    RADIAN_ARCS = prev;
  }
}
/** arctan, arcsin, arccos s'évaluent-ils en radians en ce moment ? */
export const radianArcs = (): boolean => RADIAN_ARCS;
const fromRad = (r: number): number => (RADIAN_ARCS ? r : (r * 180) / Math.PI);

/** Valeur approchée (NaN si indéfinie). */
export function evalFloat(t: Term, env: Readonly<Record<string, number>>): number {
  switch (t.k) {
    case 'num':
      return Q.ratToNumber(t.v);
    case 'sym':
      return t.n in env ? env[t.n]! : t.n === 'π' ? Math.PI : NaN;
    case 'add':
      return t.c.reduce((s, c) => s + evalFloat(c, env), 0);
    case 'mul':
      return t.c.reduce((s, c) => s * evalFloat(c, env), 1);
    case 'div': {
      const d = evalFloat(t.b, env);
      return d === 0 ? NaN : evalFloat(t.a, env) / d;
    }
    case 'pow':
      return realPow(evalFloat(t.a, env), evalFloat(t.b, env));
    case 'binom': {
      // « k parmi n » (moteur v27) : entiers naturels seulement, 0 si k > n
      const k = evalFloat(t.a, env);
      const n = evalFloat(t.b, env);
      if (!Number.isInteger(k) || !Number.isInteger(n) || k < 0 || n < 0) return NaN;
      if (k > n) return 0;
      const kk = Math.min(k, n - k);
      let r = 1;
      for (let i = 1; i <= kk; i++) r = (r * (n - kk + i)) / i;
      return r;
    }
    case 'fn': {
      const x = evalFloat(t.a, env);
      switch (t.f) {
        case 'sqrt':
          return x < 0 ? NaN : Math.sqrt(x);
        case 'abs':
          return Math.abs(x);
        case 'fact': {
          if (!Number.isInteger(x) || x < 0 || x > 170) return NaN;
          let f = 1;
          for (let i = 2; i <= x; i++) f *= i;
          return f;
        }
        case 'deg':
          return (x * Math.PI) / 180;
        case 'ln':
          return x <= 0 ? NaN : Math.log(x);
        case 'log':
          return x <= 0 ? NaN : Math.log10(x);
        case 'exp':
          return Math.exp(x);
        case 'cos':
          return Math.cos(x);
        case 'sin':
          return Math.sin(x);
        case 'tan':
          return Math.tan(x);
        case 'arccos':
          return fromRad(Math.acos(x));
        case 'arcsin':
          return fromRad(Math.asin(x));
        case 'arctan':
          return fromRad(Math.atan(x));
        case 'arg':
          return NaN;
      }
    }
  }
}
function realPow(a: number, b: number): number {
  if (a >= 0 || Number.isInteger(b)) return a ** b;
  // racine impaire d'un négatif : (−8)^(1/3) = −2
  const inv = 1 / b;
  if (
    Number.isInteger(Math.round(inv)) &&
    Math.abs(inv - Math.round(inv)) < 1e-12 &&
    Math.round(inv) % 2 !== 0
  )
    return -((-a) ** b);
  return NaN;
}

/* ------------------------------ racines rationnelles ------------------------------ */

/**
 * Racines rationnelles d'un polynôme en UNE inconnue (théorème des racines
 * rationnelles). null si le polynôme a d'autres atomes ou des coefficients trop grands.
 */
export function rationalRoots(p: Poly, name: string): Rat[] | null {
  const coefs = new Map<number, Rat>();
  for (const { m, c } of p.t.values()) {
    if (m.length > 1 || (m.length === 1 && m[0]!.a.symbol !== name)) return null;
    coefs.set(m.length ? m[0]!.e : 0, c);
  }
  if (!coefs.size) return null;
  const deg = Math.max(...coefs.keys());
  if (deg === 0) return [];
  // coefficients entiers
  let lcm = 1n;
  const g = (a: bigint, b: bigint): bigint => (b === 0n ? a : g(b, a % b));
  for (const c of coefs.values()) lcm = (lcm / g(lcm, c.d)) * c.d;
  const ints: bigint[] = [];
  for (let i = 0; i <= deg; i++) {
    const c = coefs.get(i);
    ints.push(c ? (c.n * lcm) / c.d : 0n);
  }
  const roots: Rat[] = [];
  let low = 0;
  while (ints[low] === 0n) low++;
  if (low > 0) roots.push(Q.ZERO);
  if (low === deg) return roots;
  const a0 = ints[low]! < 0n ? -ints[low]! : ints[low]!;
  const an = ints[deg]! < 0n ? -ints[deg]! : ints[deg]!;
  const dp = divisors(a0);
  const dq = divisors(an);
  if (!dp || !dq) return null;
  const seen = new Set<string>();
  for (const pp of dp)
    for (const qq of dq)
      for (const s of [1n, -1n]) {
        const r = Q.rat(s * pp, qq);
        const k = Q.ratToText(r);
        if (seen.has(k)) continue;
        seen.add(k);
        let v = Q.ZERO;
        for (let i = deg; i >= 0; i--) v = Q.add(Q.mul(v, r), Q.rat(ints[i]!));
        if (Q.isZero(v)) roots.push(r);
      }
  return roots.sort(Q.cmp);
}
function divisors(n: bigint): bigint[] | null {
  if (n > BigInt(MAX_ROOT_INPUT)) return null;
  const x = Number(n);
  const out: bigint[] = [];
  for (let d = 1; d * d <= x; d++)
    if (x % d === 0) {
      out.push(BigInt(d));
      if (d * d !== x) out.push(BigInt(x / d));
      if (out.length > 4000) return null;
    }
  return out;
}
